import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "panel", "search", "option", "group", "empty"]

  connect() {
    this.clickOutsideHandler = this.handleClickOutside.bind(this)
    this.submitHandler = this.handleFormSubmit.bind(this)
    document.addEventListener("click", this.clickOutsideHandler)
    this.formElement = this.element.closest("form")
    if (this.formElement) {
      this.formElement.addEventListener("submit", this.submitHandler)
    }
    this.syncSelectedLabel()
    this.filter("")
  }

  disconnect() {
    document.removeEventListener("click", this.clickOutsideHandler)
    if (this.formElement) {
      this.formElement.removeEventListener("submit", this.submitHandler)
    }
  }

  open() {
    this.panelTarget.hidden = false
    if (this.selectedOption() && this.searchTarget.value.trim().length > 0) {
      this.filter("")
    } else {
      this.filter()
    }
    if (this.hasSearchTarget) {
      this.searchTarget.focus()
    }
  }

  close() {
    this.panelTarget.hidden = true
  }

  select(event) {
    const option = event.currentTarget
    this.selectOption(option)
    this.close()
  }

  onInput() {
    const keyword = this.searchTarget.value.toLowerCase().trim()
    const selected = this.selectedOption()
    if (selected && selected.dataset.label.toLowerCase() !== keyword) {
      this.inputTarget.value = ""
      this.optionTargets.forEach((item) => {
        item.dataset.selected = "false"
      })
    }
    this.open()
    this.filter()
  }

  onBlur() {
    window.setTimeout(() => {
      this.commitTypedValue()
      this.close()
    }, 120)
  }

  filter(queryOverride = null) {
    const query = queryOverride === null ? (this.hasSearchTarget ? this.searchTarget.value.toLowerCase().trim() : "") : queryOverride
    let visibleCount = 0

    this.groupTargets.forEach((group) => {
      const options = group.querySelectorAll("[data-combobox-target='option']")
      let groupVisible = 0
      options.forEach((option) => {
        const haystack = option.dataset.searchText || ""
        const matched = query.length === 0 || haystack.includes(query)
        option.hidden = !matched
        if (matched) groupVisible += 1
      })
      group.hidden = groupVisible === 0
      visibleCount += groupVisible
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = visibleCount > 0
    }
  }

  onSearchKeydown(event) {
    if (event.key === "Escape") {
      this.close()
      return
    }

    if (event.key === "Enter") {
      const exact = this.visibleOptions().find((option) => option.dataset.label?.toLowerCase() === this.searchTarget.value.toLowerCase().trim())
      if (exact) {
        event.preventDefault()
        this.selectOption(exact)
        this.close()
      }
    }
  }

  handleFormSubmit() {
    this.commitTypedValue()
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  isOpen() {
    return !this.panelTarget.hidden
  }

  syncSelectedLabel() {
    const value = this.inputTarget.value
    const selected = this.optionTargets.find((option) => option.dataset.value === value)
    if (selected) {
      this.searchTarget.value = selected.dataset.label
      this.optionTargets.forEach((item) => {
        item.dataset.selected = item === selected ? "true" : "false"
      })
      return
    }

    this.searchTarget.value = ""
  }

  selectOption(option) {
    const value = option?.dataset?.value
    const label = option?.dataset?.label
    if (!value || !label) return

    this.inputTarget.value = value
    this.searchTarget.value = label
    this.optionTargets.forEach((item) => {
      item.dataset.selected = item === option ? "true" : "false"
    })
  }

  commitTypedValue() {
    const query = this.searchTarget.value.toLowerCase().trim()
    if (query.length === 0) {
      this.inputTarget.value = ""
      this.optionTargets.forEach((item) => {
        item.dataset.selected = "false"
      })
      return
    }

    const exact = this.optionTargets.find((option) => option.dataset.label?.toLowerCase() === query)
    if (exact) {
      this.selectOption(exact)
    }
  }

  selectedOption() {
    return this.optionTargets.find((option) => option.dataset.selected === "true")
  }

  visibleOptions() {
    return this.optionTargets.filter((option) => !option.hidden)
  }
}
